import * as React from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import AppQueryProvider from './AppQueryProvider';
import AppErrorBoundary from './AppErrorBoundary';
import { TooltipProvider } from "@/components/ui/tooltip-provider";
import { TeamsProvider } from '@/context/TeamsContext';
import { TeammatesProvider } from '@/context/TeammatesContext';
import { CourierProvider } from "@trycourier/react-provider";
import { notificationService } from '@/api/services/notificationService';
import { AblyProvider } from '@/context/AblyContext';
import { useContext } from 'react';
import { AuthProvider, useAuth } from '@/context/AuthContext';
import { OrganizationProvider } from '@/context/OrganizationContext';

const LoadingSpinner = () => (
    <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4" />
            <p className="text-gray-600">Initializing notifications...</p>
        </div>
    </div>
);

const AppProviders: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { sessionData } = useAuth();
    const user = sessionData?.user;
    const [courierToken, setCourierToken] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (user?.id) {
            console.log("Setting courier token");
            notificationService.authCourer().then(setCourierToken);
        }
    }, [user?.id]);

    const appContent = (
        <>
            <Toaster />
            <Sonner />
            {children}
        </>
    );

    const withProviders = (content: React.ReactNode) => (
        <AppErrorBoundary>
            <AppQueryProvider>
                <AblyProvider>
                    <TeamsProvider>
                        <TeammatesProvider>
                            <OrganizationProvider>
                                <TooltipProvider>
                                    {content}
                                </TooltipProvider>
                            </OrganizationProvider>
                        </TeammatesProvider>
                    </TeamsProvider>
                </AblyProvider>
            </AppQueryProvider>
        </AppErrorBoundary>
    );

    if (!user?.id) return withProviders(appContent);
    if (!courierToken) return <LoadingSpinner />;

    return withProviders(
        <AuthProvider >

            <CourierProvider
                userId={user.id}
                clientKey={import.meta.env.VITE_REACT_APP_COURIER_CLIENT_KEY || "MDZjNzUyMTktMTQ1Ni00MTc4LWJjMzQtZDQzZmE3M2E4NDFj"}
                authorization={courierToken}
            >
                {appContent}
            </CourierProvider>
        </AuthProvider>
    );
};

export default AppProviders;
